// src/features/records/components/RecordDetail/components/RecordContent.js
import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sanitizeText } from '../../../../../utils/sanitize';
import styles from '../RecordDetail.module.css';

const RecordContent = ({ record }) => {
  const { content } = record;

  return (
    <div className={styles.contentSection}>
      {/* Body with Markdown rendering - Typography-first approach */}
      {content?.body && (
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            // Custom components for security and styling
            a: ({ node, children, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
            // Enhanced paragraph styling
            p: ({ children }) => (
              <p className={styles.contentParagraph}>{children}</p>
            ),
            // Enhanced list styling
            ul: ({ children }) => (
              <ul className={styles.contentList}>{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className={styles.contentList}>{children}</ol>
            ),
            li: ({ children }) => (
              <li className={styles.contentListItem}>{children}</li>
            ),
            // Enhanced code styling
            code: ({ children, className }) => {
              const isCodeBlock = className?.startsWith('language-');
              return isCodeBlock ? (
                <code className={styles.contentCodeBlock}>{children}</code>
              ) : (
                <code className={styles.contentInlineCode}>{children}</code>
              );
            },
            pre: ({ children }) => (
              <pre className={styles.contentPre}>{children}</pre>
            ),
            // Enhanced blockquote styling
            blockquote: ({ children }) => (
              <blockquote className={styles.contentBlockquote}>{children}</blockquote>
            ),
            // Enhanced heading styling
            h1: ({ children }) => <h1 className={styles.contentHeading1}>{children}</h1>,
            h2: ({ children }) => <h2 className={styles.contentHeading2}>{children}</h2>,
            h3: ({ children }) => <h3 className={styles.contentHeading3}>{children}</h3>,
            h4: ({ children }) => <h4 className={styles.contentHeading4}>{children}</h4>,
            h5: ({ children }) => <h5 className={styles.contentHeading5}>{children}</h5>,
            h6: ({ children }) => <h6 className={styles.contentHeading6}>{children}</h6>,
            // Prevent script injection
            script: () => null,
            iframe: () => null,
            object: () => null,
            embed: () => null
          }}
        >
          {sanitizeText(content.body)}
        </ReactMarkdown>
      )}
    </div>
  );
};

RecordContent.propTypes = {
  record: PropTypes.shape({
    content: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string)
    }),
    links: PropTypes.shape({
      outgoing: PropTypes.arrayOf(PropTypes.object)
    })
  }).isRequired
};

export default RecordContent;